import { endpoints } from '@/api/endpoints/endpoints';
import AsyncDataTable from '@/components/core/async-data-table/async-data-table';
import FilterHeader from '@/components/core/async-data-table/filter-header';
import { DatableColumnType } from '@/components/core/async-data-table/type';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ArrowRight } from 'lucide-react';
import { useMemo } from 'react';

function MovementTypeBadge({ type }: { type: string }) {
  const isInType = type.includes('IN') || type === 'RETURN' || type === 'RECEIVE';
  const isOutType = type.includes('OUT') || type === 'ADJUSTMENT' || type === 'SALE';

  return (
    <Badge
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
        isInType
          ? 'bg-gray-800 text-white'
          : isOutType
          ? 'bg-gray-200 text-gray-800'
          : 'bg-gray-200 text-gray-800',
      )}
    >
      {type}
      <ArrowRight className="h-3 w-3" />
    </Badge>
  );
}

const StockMovementAsyncTable = () => {
  const cols: DatableColumnType[] = useMemo(
    () => [
      {
        name: 'movementTime',
        id: 'movement_time',
        label: 'Date & Time',
        searchable: true,
        orderable: true,
        datatype: 'date' as const,
        Render: ({ row }: { row: any }) => {
          if (!row.movementTime) return '-';
          return (
            <span className="text-sm text-gray-900">
              {format(new Date(row.movementTime), 'MMM d, yyyy, HH:mm')}
            </span>
          );
        },
      },
      {
        name: 'productName',
        id: 'product_name',
        label: 'Product',
        searchable: true,
        orderable: true,
        datatype: 'string' as const,
        Render: ({ row }: { row: any }) => (
          <span className="text-sm text-gray-900">{row.productName || '-'}</span>
        ),
      },
      {
        name: 'sku',
        id: 'sku',
        label: 'Variant SKU',
        searchable: false,
        orderable: false,
        datatype: 'string' as const,
        Render: ({ row }: { row: any }) => (
          <span className="text-sm text-gray-900">{row.sku || '-'}</span>
        ),
      },
      {
        name: 'movementType',
        id: 'movement_type',
        label: 'Type',
        searchable: true,
        orderable: true,
        datatype: 'string' as const,
        Render: ({ row }: { row: any }) => <MovementTypeBadge type={row.movementType || ''} />,
      },
      {
        name: 'quantity',
        id: 'quantity',
        label: 'Quantity',
        searchable: false,
        orderable: false,
        datatype: 'number' as const,
        Render: ({ row }: { row: any }) => {
          const rawQuantity = row.quantity ?? 0;
          const movementType = row.movementType || '';
          const isNegativeType = movementType === 'SALE' || movementType === 'ADJUST_OUT';
          const displayQuantity = isNegativeType ? -Math.abs(rawQuantity) : Math.abs(rawQuantity);

          return (
            <span
              className={cn(
                'text-sm font-medium',
                displayQuantity > 0 ? 'text-blue-600' : 'text-orange-600',
              )}
            >
              {displayQuantity > 0 ? '+' : ''}
              {displayQuantity}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <AsyncDataTable
      dataUrl={endpoints.inventoryLedger.list}
      columns={cols}
      autoIndex={true}
      FilterComponent={FilterHeader}
      refreshPage={false}
      showPageHeader={false}
      orderByDef="movement_time"
      orderDef="desc"
    />
  );
};

export default StockMovementAsyncTable;
